import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchCapacityPlanningSummary,
  fetchExpansionCandidates,
  fetchTopExpansionCandidates,
} from '../lib/capacityPlanningApi';
import { capacityPlanningQueryKeys } from '../lib/capacityPlanningQueryKeys';
import { sortByPriorityAndUrgency } from '../lib/capacityPlanningFormatters';

export const useCapacityPlanningSummary = () =>
  useQuery({
    queryKey: capacityPlanningQueryKeys.summary,
    queryFn: fetchCapacityPlanningSummary,
    refetchInterval: 20000,
  });

export const useExpansionCandidates = () =>
  useQuery({
    queryKey: capacityPlanningQueryKeys.candidates,
    queryFn: fetchExpansionCandidates,
    refetchInterval: 20000,
  });

export const useTopExpansionCandidates = (limit = 10) =>
  useQuery({
    queryKey: capacityPlanningQueryKeys.top(limit),
    queryFn: () => fetchTopExpansionCandidates(limit),
    refetchInterval: 20000,
  });

export const useCapacityPlanningData = () => {
  const [scopeType, setScopeType] = useState('ALL');
  const [priority, setPriority] = useState('ALL');
  const [area, setArea] = useState('');
  const [expansionType, setExpansionType] = useState('ALL');
  const [urgencyThreshold, setUrgencyThreshold] = useState(0);

  const summaryQuery = useCapacityPlanningSummary();
  const candidatesQuery = useExpansionCandidates();
  const topQuery = useTopExpansionCandidates(8);

  const allCandidates = useMemo(
    () => sortByPriorityAndUrgency(candidatesQuery.data?.items || []),
    [candidatesQuery.data?.items],
  );

  const filteredCandidates = useMemo(() => allCandidates.filter((item) => {
    if (scopeType !== 'ALL' && String(item?.scopeType || '').toUpperCase() !== scopeType) return false;
    if (priority !== 'ALL' && String(item?.priorityClass || '').toUpperCase() !== priority) return false;
    if (expansionType !== 'ALL' && String(item?.expansionType || '').toUpperCase() !== expansionType) return false;
    if (area.trim()) {
      const areaName = String(item?.areaName || '').toLowerCase();
      if (!areaName.includes(area.trim().toLowerCase())) return false;
    }
    if (Number(item?.urgencyScore || 0) < Number(urgencyThreshold || 0)) return false;
    return true;
  }), [allCandidates, scopeType, priority, expansionType, area, urgencyThreshold]);

  const isLoading = summaryQuery.isLoading || candidatesQuery.isLoading || topQuery.isLoading;
  const error = summaryQuery.error || candidatesQuery.error || topQuery.error;

  const refetch = () => {
    summaryQuery.refetch();
    candidatesQuery.refetch();
    topQuery.refetch();
  };

  return {
    summary: summaryQuery.data || {},
    candidates: filteredCandidates,
    topCandidates: topQuery.data?.items || [],
    total: filteredCandidates.length,
    filters: {
      scopeType,
      setScopeType,
      priority,
      setPriority,
      area,
      setArea,
      expansionType,
      setExpansionType,
      urgencyThreshold,
      setUrgencyThreshold,
    },
    isLoading,
    error,
    refetch,
  };
};
